import { useState, useEffect, useRef, ReactNode, FocusEvent } from 'react';
import { activationProps } from '../common/activationProps';
import { Arrow, ArrowLeft, ArrowRight, ChevronLeft } from '../icons';
import { useTranslation } from 'react-i18next';
import { WeeklyCalendar } from '../../assets/styles/components/calendar.styled';
import { Flex } from 'antd';
import { Button, Text } from '../Ant';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { ITask } from '../../interface/tasks';
import { ITransaction } from '../../interface/transaction';
import { IWorkTime } from '../../interface/workTimes';
import { formatter } from '../../constants/utilityFunctions';

dayjs.extend(isoWeek);

type Day = {
  date: Date;
  label: string;
};

type CalendarViewProps = {
  onWeekChange?: (startDate: Date, endDate: Date) => void;
  onDateChange?: (date: Date) => void;
  onSelectedDateChange?: (date: Date) => void;
  groupedMap?: Record<string, { items: ITask[] | ITransaction[] | IWorkTime[] }>;
  extracomponent?: ReactNode;
  customDate?: Date;
  initialWeekStart?: Date;
};

const CalendarView = ({
  onWeekChange,
  groupedMap,
  extracomponent,
  onDateChange,
  onSelectedDateChange,
  customDate,
  initialWeekStart,
}: CalendarViewProps) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(initialWeekStart ?? new Date());
  const [week, setWeek] = useState<Day[]>([]);
  const [selectedDate, setSelectedDate] = useState(customDate ?? new Date());

  const daysRef = useRef<HTMLDivElement>(null);
  const focusSelectedDayRef = useRef(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  useEffect(() => {
    generateWeek(currentDate);
  }, [currentDate]);

  useEffect(() => {
    if (selectedDate) {
      onSelectedDateChange?.(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!focusSelectedDayRef.current) return;
    const selectedDay = daysRef.current?.querySelector<HTMLElement>('.day.selected');
    if (!selectedDay) return;
    focusSelectedDayRef.current = false;
    selectedDay.focus();
  }, [week, selectedDate]);

  useEffect(() => {
    handleScrollVisibility();
    const container = daysRef.current;
    if (container) {
      container.addEventListener('scroll', handleScrollVisibility);
      return () => container.removeEventListener('scroll', handleScrollVisibility);
    }
    return undefined;
  }, [week]);

  useEffect(() => {
    // A scroll arrow appearing narrows the strip and can push the focused day back out of view.
    daysRef.current?.querySelector<HTMLElement>(':focus')?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [showLeftArrow, showRightArrow]);

  const generateWeek = (date: Date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    const days: Day[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      days.push({
        date: d,
        label: '',
      });
    }
    setWeek(days);
    if (onWeekChange) {
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      onWeekChange(startOfWeek, endOfWeek);
      const selectedDayOfWeek = dayjs(selectedDate).day();
      setSelectedDate(
        dayjs(startOfWeek)
          .isoWeekday(selectedDayOfWeek === 0 ? 7 : selectedDayOfWeek)
          .toDate(),
      );
    }
  };

  const handlePrevWeek = () => {
    const prevWeek = new Date(currentDate);
    prevWeek.setDate(currentDate.getDate() - 7);
    setCurrentDate(prevWeek);
  };

  const handleNextWeek = () => {
    const nextWeek = new Date(currentDate);
    nextWeek.setDate(currentDate.getDate() + 7);
    setCurrentDate(nextWeek);
  };

  const handleToday = () => {
    const today = new Date();
    // The Today button unmounts once today is selected, so focus moves to today's day instead.
    focusSelectedDayRef.current = true;
    setCurrentDate(today);
    setSelectedDate(today);
    onDateChange?.(today);
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    onDateChange?.(date);
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (date: Date) => {
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const formatWeekDateRange = (weekStart: Date): string => {
    const endDate = new Date(weekStart);
    endDate.setDate(weekStart.getDate() + 6);
    return `${formatter.dateWithoutYear(weekStart)} – ${formatter.dateWithoutYear(endDate)}`;
  };
  const formatHeader = () => {
    if (week.length === 0) return '';
    const start = week[0].date;
    return formatWeekDateRange(new Date(start));
  };

  const handleScrollVisibility = () => {
    const container = daysRef.current;
    if (container) {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(container.scrollLeft + container.clientWidth < container.scrollWidth);
    }
  };

  /** The strip scrolls sideways on narrow panes, and the browser leaves a half-hidden day as it is. */
  const revealDay = (event: FocusEvent<HTMLDivElement>) => {
    event.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const scrollDaysLeft = () => {
    daysRef.current?.scrollBy({ left: -100, behavior: 'smooth' });
  };

  const scrollDaysRight = () => {
    daysRef.current?.scrollBy({ left: 100, behavior: 'smooth' });
  };

  const shortWeekdays = [
    t('common:weekCalendar.sun'),
    t('common:weekCalendar.mon'),
    t('common:weekCalendar.tue'),
    t('common:weekCalendar.wed'),
    t('common:weekCalendar.thu'),
    t('common:weekCalendar.fri'),
    t('common:weekCalendar.sat'),
  ];
  return (
    <WeeklyCalendar>
      {extracomponent}
      <div className="calendar-header">
        <Flex vertical>
          <div className="week-range">{formatHeader()}</div>
          <Text
            data-testid="week-number"
            $color="subtitle"
            $fontsize="9px"
            $weight={500}
            $lineheight="12px"
          >
            {`(${t('common:week')} ${dayjs(selectedDate).isoWeek()})`}
          </Text>
        </Flex>
        <Flex gap={10} align="center">
          {!dayjs(selectedDate).isSame(dayjs(), 'day') && (
            <Button data-testid="today-button" type="text" onClick={handleToday}>
              {t('common:today')}
            </Button>
          )}

          <Flex align="center" gap={2}>
            <Button
              data-testid="prev-week-button"
              aria-label={t('common:dateFilter.previousWeek')}
              type="text"
              onClick={handlePrevWeek}
              icon={<ArrowLeft className="fz-20 flex-shrink-0" />}
            />
            <Button
              data-testid="next-week-button"
              aria-label={t('common:weekCalendar.nextWeek')}
              type="text"
              onClick={handleNextWeek}
              icon={<Arrow className="fz-20 flex-shrink-0" />}
            />
          </Flex>
        </Flex>
      </div>
      <div className="days-scroll-wrapper">
        {showLeftArrow && (
          <Button
            data-testid="scroll-left-button"
            aria-label={t('common:prev')}
            className="scroll-arrow left"
            onClick={scrollDaysLeft}
          >
            <ChevronLeft />
          </Button>
        )}
        <Flex justify="space-between" gap={8} className="days-container" ref={daysRef}>
          {week.map((day) => {
            const isSelectedDay = isSelected(day.date);
            const isTodayDate = isToday(day.date);
            const weekdayLetter = shortWeekdays[day.date.getDay()];
            const dateKey = dayjs(day.date).format('YYYY-MM-DD');
            let showActivity = false;
            if (groupedMap) {
              const displayKey =
                dateKey === dayjs().format('YYYY-MM-DD')
                  ? t('common:today')
                  : dayjs(day.date).format('MMM DD, YYYY');
              showActivity = groupedMap?.[displayKey]?.items?.length > 0;
            }
            return (
              <div
                key={day.date.toISOString()}
                className={`day ${isSelectedDay ? 'selected' : ''} ${isTodayDate ? 'today' : ''}`}
                aria-pressed={isSelectedDay}
                aria-label={dayjs(day.date).format('dddd, LL')}
                onFocus={revealDay}
                {...activationProps(() => handleDateClick(day.date))}
              >
                <div data-testid={`weekday-${dateKey}`} className="weekday">
                  {weekdayLetter}
                </div>
                <div data-testid={`day-number-${dateKey}`} className="day-number">
                  {day.date.getDate()}
                </div>
                {showActivity && <span data-testid={`activity-${dateKey}`} className="activity" />}
              </div>
            );
          })}
        </Flex>

        {showRightArrow && (
          <Button
            data-testid="scroll-right-button"
            aria-label={t('common:onboarding.next')}
            className="scroll-arrow right"
            onClick={scrollDaysRight}
          >
            <ArrowRight />
          </Button>
        )}
      </div>
    </WeeklyCalendar>
  );
};

export default CalendarView;
